import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { chatThreadTitleAtom } from '@frontend/atoms';
import { useThread } from '@frontend/hooks/useThread';

type ThreadUpdatedEventPayload = {
  threadId?: string | null;
  title?: string | null;
};

// Might change in future
type ThreadDeletedEventPayload = ThreadUpdatedEventPayload;

type ThreadTitleChangedEventPayload = {
  title?: string | null;
};

const EVENT_NAME = {
  OPAL_THREAD_CREATED: 'opal::thread::created',
  OPAL_THREAD_CHANGED: 'opal::thread::changed',
  OPAL_THREAD_DELETED: 'opal::thread::deleted',
  OPAL_THREAD_TITLE_CHANGED: 'opal::thread::titleChanged',
  OPAL_CHAT_SET_THREAD: 'opal::chat::setThread',
};

function isThreadUpdatedEvent(event: Event): event is CustomEvent<ThreadUpdatedEventPayload> {
  return (
    event instanceof CustomEvent &&
    (event.type === EVENT_NAME.OPAL_THREAD_CHANGED || event.type === EVENT_NAME.OPAL_THREAD_CREATED)
  );
}

function isThreadDeletedEvent(event: Event): event is CustomEvent<ThreadDeletedEventPayload> {
  return event instanceof CustomEvent && event.type === EVENT_NAME.OPAL_THREAD_DELETED;
}

function isThreadTitleChangedEvent(
  event: Event
): event is CustomEvent<ThreadTitleChangedEventPayload> {
  return event instanceof CustomEvent && event.type === EVENT_NAME.OPAL_THREAD_TITLE_CHANGED;
}

export function ChatEventListener({ children }: { children: React.ReactNode }) {
  const { threadId, setThreadId } = useThread();
  const setChatThreadTitle = useSetAtom(chatThreadTitleAtom);

  useEffect(() => {
    const handleThreadUpdated = (event: Event) => {
      if (isThreadUpdatedEvent(event)) {
        const { threadId: newThreadId, title } = event.detail;
        setThreadId(newThreadId);
        // Update title if provided in thread update event
        if (title !== undefined) {
          setChatThreadTitle(title);
        }
      }
    };
    const handleThreadDeleted = (event: Event) => {
      if (isThreadDeletedEvent(event)) {
        const { threadId: deletedThreadId } = event.detail;
        if (threadId === deletedThreadId) {
          setThreadId(undefined);
          setChatThreadTitle(null);
        }
      }
    };
    const handleThreadTitleChanged = (event: Event) => {
      if (isThreadTitleChangedEvent(event)) {
        const { title } = event.detail;
        setChatThreadTitle(title ?? null);
      }
    };

    window.addEventListener(EVENT_NAME.OPAL_THREAD_CHANGED, handleThreadUpdated);
    window.addEventListener(EVENT_NAME.OPAL_THREAD_CREATED, handleThreadUpdated);
    window.addEventListener(EVENT_NAME.OPAL_THREAD_DELETED, handleThreadDeleted);
    window.addEventListener(EVENT_NAME.OPAL_THREAD_TITLE_CHANGED, handleThreadTitleChanged);

    return () => {
      window.removeEventListener(EVENT_NAME.OPAL_THREAD_CHANGED, handleThreadUpdated);
      window.removeEventListener(EVENT_NAME.OPAL_THREAD_CREATED, handleThreadUpdated);
      window.removeEventListener(EVENT_NAME.OPAL_THREAD_DELETED, handleThreadDeleted);
      window.removeEventListener(EVENT_NAME.OPAL_THREAD_TITLE_CHANGED, handleThreadTitleChanged);
    };
  }, [threadId, setThreadId, setChatThreadTitle]);

  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME.OPAL_CHAT_SET_THREAD, {
        detail: { threadId },
        bubbles: true,
      })
    );
  }, [threadId]);

  return <>{children}</>;
}
